import { useEffect, useMemo, useState } from "react";
import {
  useGetFactoryQuery,
  useCreateFactoryMutation,
  useUpdateFactoryMutation,
} from "../../../../redux/api";
import type { Factory as FactoryModel } from "../../../../redux/api";
import { validateEmail, validatePhone } from "../../../../utils/validation";
import { PhoneInput } from "../../../../components/PhoneInput";
import { Modal } from "../../../../components/Modal";
import { EntityMeta } from "../../../../components/EntityMeta";
import { Field } from "../../../../components/Field";
import { statusTone } from "../../../../utils/status";

interface FactoryFormModalProps {
  open: boolean;
  /** null = create */
  id: string | null;
  onClose: () => void;
}

type FactoryForm = {
  name: string;
  code: string;
  email: string;
  phone: string;
  gstin: string;
  contactPerson: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  leadTimeDays: string;
  status: string;
};

const emptyForm: FactoryForm = {
  name: "",
  code: "",
  email: "",
  phone: "",
  gstin: "",
  contactPerson: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  country: "India",
  postalCode: "",
  leadTimeDays: "",
  status: "active",
};

const formFromFactory = (f: FactoryModel): FactoryForm => ({
  name: f.name ?? "",
  code: f.code ?? "",
  email: f.email ?? "",
  phone: f.phone ?? "",
  gstin: f.gstin ?? "",
  contactPerson: f.contactPerson ?? "",
  addressLine1: f.addressLine1 ?? "",
  addressLine2: f.addressLine2 ?? "",
  city: f.city ?? "",
  state: f.state ?? "",
  country: f.country ?? "India",
  postalCode: f.postalCode ?? "",
  leadTimeDays: f.leadTimeDays != null ? String(f.leadTimeDays) : "",
  status: f.status ?? "active",
});

const num = (value: string): number | undefined =>
  value.trim() === "" ? undefined : Number(value);

// Inventory → Factory add/edit popup.
export const FactoryFormModal = ({ open, id, onClose }: FactoryFormModalProps) => {
  const [activeId, setActiveId] = useState<string | null>(id);
  const isEdit = !!activeId;

  const { data: existing } = useGetFactoryQuery(activeId ?? "", {
    skip: !activeId,
  });

  const [createFactory, { isLoading: creating }] = useCreateFactoryMutation();
  const [updateFactory, { isLoading: updating }] = useUpdateFactoryMutation();

  const [form, setForm] = useState<FactoryForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const saving = creating || updating;

  useEffect(() => {
    if (!open) return;
    setActiveId(id);
    setFormError(null);
    setFieldErrors({});
    if (id && existing && existing.id === id) {
      setForm(formFromFactory(existing));
    } else if (!id) {
      setForm(emptyForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  useEffect(() => {
    if (existing) setForm(formFromFactory(existing));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, existing?.updatedAt]);

  const setField = (key: keyof FactoryForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const baseline = useMemo(
    () => (existing ? formFromFactory(existing) : emptyForm),
    [existing],
  );
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const handleSave = async () => {
    setFormError(null);

    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = "Name is required.";
    const emailError = validateEmail(form.email, false);
    if (emailError) errors.email = emailError;
    const phoneError = validatePhone(form.phone);
    if (phoneError) errors.phone = phoneError;

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const payload = {
      name: form.name.trim(),
      code: form.code.trim() || undefined,
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      gstin: form.gstin.trim() || undefined,
      contactPerson: form.contactPerson.trim() || undefined,
      addressLine1: form.addressLine1.trim() || undefined,
      addressLine2: form.addressLine2.trim() || undefined,
      city: form.city.trim() || undefined,
      state: form.state.trim() || undefined,
      country: form.country.trim() || undefined,
      postalCode: form.postalCode.trim() || undefined,
      leadTimeDays: num(form.leadTimeDays),
      status: form.status,
    };

    try {
      if (isEdit && activeId) {
        await updateFactory({ id: activeId, input: payload }).unwrap();
      } else {
        await createFactory({ ...payload }).unwrap();
      }
      onClose();
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to save factory.",
      );
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? "Edit factory" : "New factory"}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create factory"}
          </button>
        </>
      }
    >
      <EntityMeta
        id={activeId ?? undefined}
        status={isEdit ? { label: form.status, tone: statusTone(form.status) } : undefined}
      />

      {formError ? <div className="form-error">{formError}</div> : null}

      <div className="form-grid">
        <Field label="Name *" error={fieldErrors.name}>
          <input
            value={form.name}
            onChange={(e) => {
              setField("name", e.target.value);
              setFieldErrors((prev) => (prev.name ? { ...prev, name: undefined } : prev));
            }}
          />
        </Field>
        <label className="field">
          Code
          <input
            value={form.code}
            onChange={(e) => setField("code", e.target.value)}
            placeholder="FAC-001"
          />
        </label>
        <Field label="Email" error={fieldErrors.email}>
          <input
            type="email"
            value={form.email}
            onChange={(e) => {
              setField("email", e.target.value);
              setFieldErrors((prev) => (prev.email ? { ...prev, email: undefined } : prev));
            }}
          />
        </Field>
        <Field label="Phone" error={fieldErrors.phone}>
          <PhoneInput
            value={form.phone}
            onChange={(v) => {
              setField("phone", v);
              setFieldErrors((prev) => (prev.phone ? { ...prev, phone: undefined } : prev));
            }}
          />
        </Field>
        <label className="field">
          GSTIN
          <input
            value={form.gstin}
            onChange={(e) => setField("gstin", e.target.value)}
          />
        </label>
        <label className="field">
          Contact person
          <input
            value={form.contactPerson}
            onChange={(e) => setField("contactPerson", e.target.value)}
          />
        </label>
        <label className="field field--full">
          Address line 1
          <input
            value={form.addressLine1}
            onChange={(e) => setField("addressLine1", e.target.value)}
          />
        </label>
        <label className="field field--full">
          Address line 2
          <input
            value={form.addressLine2}
            onChange={(e) => setField("addressLine2", e.target.value)}
          />
        </label>
        <label className="field">
          City
          <input
            value={form.city}
            onChange={(e) => setField("city", e.target.value)}
          />
        </label>
        <label className="field">
          State
          <input
            value={form.state}
            onChange={(e) => setField("state", e.target.value)}
          />
        </label>
        <label className="field">
          Country
          <input
            value={form.country}
            onChange={(e) => setField("country", e.target.value)}
          />
        </label>
        <label className="field">
          Postal code
          <input
            value={form.postalCode}
            onChange={(e) => setField("postalCode", e.target.value)}
          />
        </label>
        <label className="field">
          Lead time (days)
          <input
            type="number"
            value={form.leadTimeDays}
            onChange={(e) => setField("leadTimeDays", e.target.value)}
          />
        </label>
        <label className="field">
          Status
          <select
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
      </div>
    </Modal>
  );
};
